import { useEffect, useState } from 'react';
import {
	Button,
	TextField,
	Grid,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Backdrop,
	CircularProgress,
	Alert
} from '@mui/material';

import ReCAPTCHA from "react-google-recaptcha";
import axios from 'axios';
import { getClientSettings } from '../../utilities/utilities';
import { Link as RouterLink } from 'react-router-dom';

const PreRegister = () => {
	const [email, setEmail] = useState('');
	const [confirmEmail, setConfirmEmail] = useState('');
	const [password, setPassword] = useState('');
	const [emailError, setEmailError] = useState('');
	const [confirmEmailError, setConfirmEmailError] = useState('');
	const [isEpiqEmail, setIsEpiqEmail] = useState(false);
	const [isCaptchaEnabled, setIsCaptchaEnabled] = useState(false);
	const [captchaToken, setCaptchaToken] = useState(null);
	const [reCaptchaSiteKey, setReCaptchaSiteKey] = useState('invalid_site_key');
	const [canRegister, setCanRegister] = useState(false);

	useEffect(() => {
		const fetchConfig = async () => {
			try {
				const response = await axios.get('/api/captcha/enabled');
				setIsCaptchaEnabled(response.data);
			} catch (error) {
				throw new Error('Failed to determine reCaptcha availability.');
			}

			const clientSettings = await getClientSettings();
			setReCaptchaSiteKey(clientSettings.reCaptchaSiteKey);
		};

		fetchConfig();
	}, []);

	useEffect(() => {
		if (!email || !confirmEmail || !captchaToken || !!emailError || !!confirmEmailError) {
			setCanRegister(false);
			return;
		}

		if (isEpiqEmail && !password) {
			setCanRegister(false);
			return;
		}

		setCanRegister(true);
	}, [email, confirmEmail, emailError, confirmEmailError, captchaToken, password, isEpiqEmail]);

	const validateEmail = (emailToValidate: string) => {
		const validRegex = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{1,3}))$/;
		return validRegex.test(emailToValidate);
	};

	const handleEmailValidation = () => {
		setAlertMessage('');

		if (email && !validateEmail(email)) {
			setEmailError('Please enter a valid email address');
		} else {
			setEmailError('');
		}

		if (confirmEmail && !validateEmail(confirmEmail)) {
			setConfirmEmailError('Please enter a valid email address');
		} else {
			setConfirmEmailError('');
		}

		if (email && confirmEmail && email.toLowerCase() !== confirmEmail.toLowerCase()) {
			setConfirmEmailError('The Email Addresses must match');
		}

		const epiqEmailRegex = /@.*epiq.*\.\w{2,63}$/;

		// if there are no email errors and the user name part of the email address contains "epiq", then set isEpiqEmail to true
		if (!emailError && !confirmEmailError && email && epiqEmailRegex.test(email)) {
			setIsEpiqEmail(true);
		} else {
			setIsEpiqEmail(false);
		}
	};

	const handleCaptchaVerify = async (token: any) => {
		setCaptchaToken(token);
	};

	const [showBackdrop, setShowBackdrop] = useState(false);

	const handleSubmit = async (e: { preventDefault: () => void; }) => {
		e.preventDefault();

		try {
			setAlertMessage('');
			setShowBackdrop(true);

			// send the email address and the captcha token to the server for validation
			await axios.post('/api/auth/checkRegistration', {
				email: email,
				captchaToken: captchaToken,
				epiqResponse: password
			});

			if (isEpiqEmail) {
				// registering an Epiq user automatically logs them in, so navigate to the dashboard
				window.location.href = `/dashboard`;
				return;
			}

			// navigate to the registration page and pass the email address as a query parameter
			window.location.href = `/Register/${email}`;

		} catch (error: any) {
			console.error('User pre-registration failed.', error);

			if (error.response.data.message) {
				setAlertMessage(error.response.data.message);
			}
		}
		finally {
			setShowBackdrop(false);
		}
	};

	const handleClose = (event: any, reason: string) => {
	};

	const [alertMessage, setAlertMessage] = useState('');
	const [alertSeverity, setAlertSeverity] = useState<'error' | 'warning' | 'info' | 'success'>('warning');

	return (
		<Dialog open={true} onClose={handleClose}>
			<Backdrop
				sx={(theme) => ({ color: '#fff', zIndex: theme.zIndex.drawer + 1 })}
				open={showBackdrop}
			>
				<CircularProgress color="inherit" />
			</Backdrop>

			<DialogTitle>Register</DialogTitle>

			<DialogContent>
				{/*login button*/}
				<Grid
					container
					spacing={2}
					direction="column"
					justifyContent="center"
					alignItems="end"
				>
					<Grid item xs={3}>
						<Button variant="outlined" component={RouterLink} to={"/authentication/login"}>Login</Button>
					</Grid>
				</Grid>

				{/*email and epiq password entry*/}
				<Grid
					container
					spacing={2}
					direction="column"
					justifyContent="center"
					alignItems="center"
				>
					<Grid item xs={12}>
						<TextField
							autoFocus
							fullWidth
							label="Email"
							margin="normal"
							value={email}
							onChange={(e) => setEmail(e.target.value)}
							onBlur={handleEmailValidation}
							error={!!emailError}
							helperText={emailError}
							required
							variant='standard'
							InputLabelProps={{
								shrink: true,
								style: {
									color: "black",
								},
							}}
						/>

						<TextField
							fullWidth
							label="Confirm Email"
							margin="normal"
							value={confirmEmail}
							onChange={(e) => setConfirmEmail(e.target.value)}
							onBlur={handleEmailValidation}
							error={!!confirmEmailError}
							helperText={confirmEmailError}
							required
							variant='standard'
							InputLabelProps={{
								shrink: true,
								style: {
									color: "black",
								}, } }
						/>

						{isEpiqEmail && (
							<TextField
								fullWidth
								type="password"
								label="Epiq Password"
								margin="normal"
								value={password}
								onChange={(e) => setPassword(e.target.value)}
								required
								variant='standard'
								InputLabelProps={{
									shrink: true,
									style: {
										color: "black",
									}
								}}
								/>
						)}
					</Grid>
				</Grid>

				{/*captcha*/}
				<Grid
					container
					spacing={2}
					direction="column"
					justifyContent="center"
					alignItems="center"
				>
					<Grid item xs={12}>
						{isCaptchaEnabled && (
							<ReCAPTCHA
								sitekey={reCaptchaSiteKey}
								onChange={handleCaptchaVerify}
							/>
						)}
					</Grid>
				</Grid>

				{/* Alert for validation messages */}
				{alertMessage && (
					<Alert
						severity={alertSeverity}
						sx={{ mb: 2, width: '100%', marginTop: '1rem' }}
						onClose={() => setAlertMessage('')}
					>
						{alertMessage}
					</Alert>
				)}
			</DialogContent>

			<DialogActions>
				<Button
					fullWidth
					variant="contained"
					type="button"
					onClick={handleSubmit}
					disabled={!canRegister}
					sx={{
						"&:focus": {
							backgroundColor: "#253746 !important", // Keep the default background color on focus
						},
					}}
				>
					Register
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default PreRegister;
